import { RegulationDocument } from 'components/common-form/sidePanel/docToolbar/regulatory/model';
import { useDownloadDlpDocument } from 'hooks/useDownloadDlpDocument';
import { useTranslation } from 'hooks/useTranslation';
import { useUserNotification } from 'hooks/useUserNotification';
import { IntlKey } from 'locales';
import type { AnalyticCategoryActions, WorkspaceAnalyticCategory } from 'models';
import { useCallback, useContext } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import type { IRegulatoryRejection } from 'state/regulatoryDocuments';

export function useOnDownloadDocument({
  errorLabelKey,
  event,
}: {
  errorLabelKey: IntlKey;
  event: AnalyticCategoryActions[WorkspaceAnalyticCategory];
}) {
  const category = useContext(WorkspaceAnalyticCategoryContext);
  const downloadErrorMessage = useTranslation(errorLabelKey);
  const { isDownloading, downloadDlpDocument } = useDownloadDlpDocument(downloadErrorMessage);
  const downloadPublic = useCallback(
    (uri: string, language: string) => {
      logAnalytics.trackEvent(category, event, language);
      window.open(uri, '_blank');
    },
    [category, event],
  );

  const downloadPrivate = useCallback(
    (token: string, language: string) => {
      logAnalytics.trackEvent(category, event, language);
      downloadDlpDocument(token);
    },
    [category, event, downloadDlpDocument],
  );

  const onDownload = useCallback(
    (doc: RegulationDocument) => {
      if (doc.public !== false && doc.uri !== undefined) {
        downloadPublic(doc.uri, doc.language);
        return;
      }
      if (doc.public === false && doc.documentToken !== undefined) {
        downloadPrivate(doc.documentToken, doc.language);
        return;
      }
    },
    [downloadPublic, downloadPrivate],
  );

  const userNotification = useUserNotification();

  const onShowError = useCallback(
    (rejections: IRegulatoryRejection[]) => () => {
      const content =
        rejections.length > 0 ? rejections.map(r => r.rejectionMessage ?? r.originMessage) : [downloadErrorMessage];
      userNotification.error({ title: { id: 'toast.termsheet' }, content });
    },
    [userNotification, downloadErrorMessage],
  );

  return { onDownload, isDownloading, onShowError };
}
